import moment from "moment";

/**
 * Given a list of bank statements (of a given bank account), return the dates (as ISO string) of
 * months not yet covered by a bank statement over the given period (intended as number of months),
 * excluding those preceeding the company creation.
 * @param {Object} data the fetched bank-statement list
 * @param {string} startingDate date at which statement-check should start
 * (immatriculation date or account-opening date)
 * @param {number} period the number of last months to be checked
 * @returns {Array} the array of ISO-string dates not covered by a bank statement
 */
export function getMissingBankStatements(data, startingDate, period = 12) {
  let today = moment();
  let months = Array.from(
    { length: period },
    (_, i) =>
      moment(today)
        .subtract(i + 1, "months")
        .toISOString()
        .substring(0, 8) + "15",
  );
  const imm_year_month = startingDate.substring(0, 7);
  let coveredMonths = data.map((statement) => statement.period);
  return months.filter(
    (month) => month >= imm_year_month && !coveredMonths.includes(month),
  );
}

/**
 * Given a list of financial statements (of a given company), return the years (as string) not yet
 * covered by a statement over the given period (intended as number of years), excluding those
 * preceeding the company creation.
 * @param {Object} data the fetched statement list
 * @param {string} immatriculationDate the immatriculation date of the company
 * @param {number} period the number of years to be checked
 * @returns {Array} the array of years as string not covered by a financial statement
 */
export function getMissingFinancialStatements(
  data,
  immatriculationDate,
  period = 3,
) {
  let today = moment();
  let years = Array.from({ length: period }, (_, i) =>
    moment(today)
      .subtract(i + 1, "years")
      .year()
      .toString(),
  );
  const imm_year = immatriculationDate.substring(0, 4);
  let coveredYears = data.map((statement) => statement.year);
  return years.filter(
    (year) => year >= imm_year && !coveredYears.includes(year),
  );
}

/**
 * Capitalize string
 * @param {string} str string to capitalize
 * @returns capitalized string
 */
export function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Delete empty query params and create url
 * @param {string} baseURL base url / endpoint
 * @param {Object} queryParamsObj query parameters object
 * @returns {string} url
 */
export function createURLSearchParams(baseURL, queryParamsObj = null) {
  if (queryParamsObj) {
    const queryParamsURL = new URLSearchParams(
      Object.fromEntries(
        Object.entries(queryParamsObj)
          .map((entry) => {
            const value = entry[1];
            if (value) return entry;
            return null;
          })
          .filter((entry) => entry),
      ),
    ).toString();
    baseURL = [baseURL, queryParamsURL && `?${queryParamsURL}`].join("");
  }

  return baseURL;
}

/**
 * Compute luminosity of a colour in hexadecimal code and flag whether it needs white or black
 * contrast
 * @param {string} colour - The colour value
 * @returns {boolean} - Tell if colour is dark and needs white contrast
 */
export function isDim(colour) {
  if (!colour.startsWith("#")) {
    colour = getComputedStyle(document.documentElement)
      .getPropertyValue(colour.replace("var(", "").replace(")", ""))
      .trim();
  }

  const RGB = [
    parseInt(colour.substring(1, 3), 16),
    parseInt(colour.substring(3, 5), 16),
    parseInt(colour.substring(5, 7), 16),
  ];
  // Normalise values between 0 and 1
  const norm = RGB.map((x) => x / 255);

  // Apply gamma correction
  const a = norm.map((x) =>
    x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4),
  );
  // Compute relative luminosity
  const l = 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];

  // Tell if colour is dark
  return l <= Math.sqrt(1.05 * 0.05) - 0.05;
}

/**
 * Return reseller company associated to given company
 * @param {Object} company the given company
 * @returns {Object} its reseller company
 */
export function getReseller(company) {
  return company?.is_reseller ? company : company?.reseller;
}

export function formatSIREN(code = "123456789") {
  return code.replace(/(\w{3})(?=\w)/g, "$1 ");
}
